import * as Cesium from 'cesium'
import CesiumTools from './CesiumTools'

export const EarthMesh = {
  data() {
    return {
      selector: null,
      rectangleSelector: new Cesium.Rectangle(),
      cartesian: new Cesium.Cartesian3(),
      tempCartographic: new Cesium.Cartographic(),
      firstPoint: new Cesium.Cartographic(),
      globalMeshesPC: null,
      firstPointSet: false,
      mouseDown: false,
      latitudeLinesPC: null,
      longitudeLinesPC: null,
      meshPointsPC: null,
      meshesPrimitiveCollection: null,
      pointsList: [],
      latitudeSteps: [],
      numberOfLatitudes: 0,
      numberOfLongitudes: 0,
      lonStep: 0,
      cameraShouldFly: true,
      defaultRectangleWidth: 0.3
    }
  },
  methods: {
    initMeshesPC() {
      this.latitudeLinesPC = new Cesium.PrimitiveCollection()
      this.longitudeLinesPC = new Cesium.PrimitiveCollection()
      this.meshesPrimitiveCollection = new Cesium.PrimitiveCollection()
      this.meshPointsPC = new Cesium.PointPrimitiveCollection()

      this.globalMeshesPC = new Cesium.PrimitiveCollection()
      this.globalMeshesPC.add(this.latitudeLinesPC)
      this.globalMeshesPC.add(this.longitudeLinesPC)
      this.globalMeshesPC.add(this.meshesPrimitiveCollection)
      this.globalMeshesPC.add(this.meshPointsPC)
      return this.viewer.scene.primitives.add(this.globalMeshesPC)
    },

    updateMeshPoints(pointsList) {
      this.pointsList = pointsList
    },

    updateLonLatNumbers(pointsList) {
      let [numberOfLatitudes, numberOfLongitudes] = [0, 0]
      let prevLat = NaN
      pointsList.forEach((point) => {
        if (point.lat !== prevLat) {
          numberOfLatitudes++
          prevLat = point.lat
        }
      })
      numberOfLongitudes = pointsList.length / numberOfLatitudes
      if (numberOfLongitudes >= 2) {
        this.lonStep = Math.min(
          Math.abs(Math.abs(pointsList[1].lon) - Math.PI) +
            Math.abs(Math.abs(pointsList[0].lon) - Math.PI),
          Math.abs(pointsList[1].lon - pointsList[0].lon)
        )
        // case when west = 180 and east = -180, put very small step instead of 0
        if (this.lonStep === 0) {
          this.lonStep = 0.001
        }
      } else {
        this.lonStep = 0
      }

      this.numberOfLatitudes = numberOfLatitudes
      this.numberOfLongitudes = numberOfLongitudes
    },

    updateLatitudeParameters(pointsList) {
      let listOfLatSteps = []
      let currentLatStep

      if (this.numberOfLatitudes >= 2) {
        for (let iLat = 0; iLat < this.numberOfLatitudes - 1; iLat++) {
          let currentLatIndex =
            (iLat * pointsList.length) / this.numberOfLatitudes
          let nextLatIndex =
            ((1 + iLat) * pointsList.length) / this.numberOfLatitudes
          currentLatStep = Math.abs(
            pointsList[nextLatIndex].lat - pointsList[currentLatIndex].lat
          )
          listOfLatSteps.push(currentLatStep)
        }
        listOfLatSteps.push(currentLatStep) //for last value, use same as previous one
      } else {
        currentLatStep = Math.abs(pointsList[0].lat)
        //currentLatStep = Math.PI * 2
        listOfLatSteps.push(currentLatStep)
      }

      this.latitudeSteps = listOfLatSteps
    },

    enableRectangles() {
      const screenSpaceEventHandler = new Cesium.ScreenSpaceEventHandler(
        this.viewer.scene.canvas
      )

      //Draw the selector while the user drags the mouse while holding shift
      screenSpaceEventHandler.setInputAction(
        this.drawSelector,
        Cesium.ScreenSpaceEventType.MOUSE_MOVE,
        Cesium.KeyboardEventModifier.SHIFT
      )

      screenSpaceEventHandler.setInputAction(
        this.startClickShift,
        Cesium.ScreenSpaceEventType.LEFT_DOWN,
        Cesium.KeyboardEventModifier.SHIFT
      )

      screenSpaceEventHandler.setInputAction(
        this.endClickShift,
        Cesium.ScreenSpaceEventType.LEFT_UP,
        Cesium.KeyboardEventModifier.SHIFT
      )

      this.selector = this.viewer.entities.add({
        selectable: false,
        show: false,
        rectangle: {
          coordinates: new Cesium.CallbackProperty(
            this.getSelectorLocation,
            false
          ),
          height: 0,
          outline: new Cesium.ConstantProperty(true),
          outlineColor: Cesium.Color.WHITE,
          granularity: Cesium.Math.RADIANS_PER_DEGREE * 6,
          material: Cesium.Color.RED.withAlpha(0.3)
        }
      })
    },

    drawSelector(movement) {
      if (!this.mouseDown) {
        return
      }

      this.cartesian = this.viewer.camera.pickEllipsoid(
        movement.endPosition,
        this.viewer.scene.globe.ellipsoid,
        this.cartesian
      )

      if (this.cartesian) {
        //mouse cartographic
        this.tempCartographic = Cesium.Cartographic.fromCartesian(
          this.cartesian,
          Cesium.Ellipsoid.WGS84,
          this.tempCartographic
        )

        if (!this.firstPointSet) {
          Cesium.Cartographic.clone(this.tempCartographic, this.firstPoint)
          this.firstPointSet = true
        } else {
          let west = this.firstPoint.longitude
          let east = this.tempCartographic.longitude
          let north = CesiumTools.getMaxLonRadians(
            this.tempCartographic.latitude,
            this.firstPoint.latitude
          )
          let south = CesiumTools.getMinLatRadians(
            this.tempCartographic.latitude,
            this.firstPoint.latitude
          )

          this.rectangleSelector.east = east
          this.rectangleSelector.west = west
          this.rectangleSelector.north = north
          this.rectangleSelector.south = south
          this.selector.show = true
        }
      }
    },

    getSelectorLocation(__, result) {
      return Cesium.Rectangle.clone(this.rectangleSelector, result)
    },

    startClickShift() {
      this.removeAllPrimitives()
      this.mouseDown = true
      this.selector.rectangle.coordinates = new Cesium.CallbackProperty(
        this.getSelectorLocation,
        false
      )
    },

    endClickShift() {
      this.mouseDown = false
      this.firstPointSet = false
      this.selector.rectangle.coordinates = this.rectangleSelector
      this.$emit('coordinatesChanged', {
        west: CesiumTools.toDegrees(this.rectangleSelector.west),
        east: CesiumTools.toDegrees(this.rectangleSelector.east),
        south: CesiumTools.toDegrees(this.rectangleSelector.south),
        north: CesiumTools.toDegrees(this.rectangleSelector.north)
      })
      this.selector.show = false
      this.flyToRectangle()
    },

    createRectanglePrimitives(meshesList, listOfPointsList) {
      this.removeAllPrimitives()
      if (!meshesList || !meshesList.length) {
        return
      }
      let rectangleInstances = []
      meshesList.forEach((mesh, index) => {
        if (!mesh) {
          return
        }
        if (listOfPointsList && listOfPointsList[index]) {
          this.updateMeshPoints(listOfPointsList[index])
          this.updateLonLatNumbers(this.pointsList)
          this.updateLatitudeParameters(this.pointsList)
        }
        const coordinates = this.getCoordinatesFromMesh(mesh)
        if (!coordinates) {
          return
        }
        let rectangle = new Cesium.Rectangle()

        const west = CesiumTools.avoidExtremeLongitudes(coordinates.west, false)
        const east = CesiumTools.avoidExtremeLongitudes(coordinates.east, false)

        rectangle.west = west
        rectangle.east = east
        let north = CesiumTools.getMaxLatRadians(
          coordinates.south,
          coordinates.north
        )
        let south = CesiumTools.getMinLatRadians(
          coordinates.south,
          coordinates.north
        )
        south = CesiumTools.avoidExtremeLatitudes(coordinates.south, false)
        north = CesiumTools.avoidExtremeLatitudes(coordinates.north, false)
        rectangle.south = south
        rectangle.north = north

        this.rectangleSelector = rectangle
        let closeLines = false
        if (mesh.rangeType !== 'NONE') {
          this.createMesh(closeLines)
        }
        let newWest, newEast
        let rectangleContainingMesh
        let westernRectangle
        let westernRectangleWest = 0
        let westernRectangleEast = 0
        let needWesternRectangle = false
        let easternRectangle
        let easternRectangleWest = 0
        let easternRectangleEast = 0
        let needEasternRectangle = false
        let newValueEastWith1longitude = 0
        let newValueWestWith1longitude = 0

        if (mesh.rangeType === 'NONE') {
          rectangleContainingMesh = rectangle
        } else {
          let [minLon, maxLon, minLat, maxLat] = this.getMinMaxLonLat()

          let newSouth = CesiumTools.clampLatitude(
            minLat - this.latitudeSteps[0] / 2
          )
          let newNorth = CesiumTools.clampLatitude(
            maxLat + this.latitudeSteps[this.numberOfLatitudes - 1] / 2
          )

          const firstLon = this.pointsList[0].lon
          const lastLon = this.pointsList[this.pointsList.length - 1].lon
          newWest = Math.max(
            firstLon - this.lonStep / 2,
            CesiumTools.avoidExtremeLongitudes(-Math.PI, false)
          )
          newEast = Math.min(
            lastLon + this.lonStep / 2,
            CesiumTools.avoidExtremeLongitudes(Math.PI, false)
          ) /* 
          if (west + Math.PI < 1e-3 && east - Math.PI < 1e-3) {
            newWest = CesiumTools.clampToPI(-Cesium.Math.PI)
            newEast = CesiumTools.clampToPI(Cesium.Math.PI)
          } */
          let moreThan1Longitude = this.lonStep !== 0
          if (
            moreThan1Longitude &&
            Math.abs(Math.abs(west) - Math.PI) < 1e-3 &&
            west > east
          ) {
            newWest = CesiumTools.clampToPI(west)
            newEast = CesiumTools.clampToPI(east + this.lonStep / 2)
          }

          if (
            moreThan1Longitude &&
            ((west > east && Math.abs(newWest - newEast) < 1e-3) ||
              (Math.abs(west + east) < 1e-3 && Math.abs(west) === Math.PI))
          ) {
            newWest = CesiumTools.clampToPI(-Cesium.Math.PI)
            newEast = CesiumTools.clampToPI(Cesium.Math.PI)
          }

          //When only 1 longitude, draw a rectangle around this longitude
          if (Math.abs(newWest - newEast) < 1e-3) {
            newValueWestWith1longitude =
              newWest - this.defaultRectangleWidth / 2
            newWest = Math.max(
              newValueWestWith1longitude,
              CesiumTools.avoidExtremeLongitudes(-Math.PI, false)
            )

            newValueEastWith1longitude =
              newEast + this.defaultRectangleWidth / 2
            newEast = Math.min(
              newValueEastWith1longitude,
              CesiumTools.avoidExtremeLongitudes(Math.PI, false)
            )
          }

          let conditionFirstLongitudeOver180andMoreThan1 =
            firstLon - this.lonStep / 2 <
            CesiumTools.avoidExtremeLatitudes(-Math.PI, false)

          let conditionFirstLongitudeOver180andOnly1 =
            this.lonStep === 0 &&
            (newValueWestWith1longitude <
              CesiumTools.avoidExtremeLatitudes(-Math.PI, false) ||
              Math.abs(
                newValueWestWith1longitude -
                  CesiumTools.avoidExtremeLatitudes(-Math.PI, false)
              ) < 1e-3)

          if (
            conditionFirstLongitudeOver180andMoreThan1 ||
            conditionFirstLongitudeOver180andOnly1
          ) {
            let widthRectangle = conditionFirstLongitudeOver180andMoreThan1
              ? firstLon - this.lonStep / 2
              : newValueWestWith1longitude
            needWesternRectangle = true
            westernRectangleWest = CesiumTools.clampLongitude(widthRectangle)
            westernRectangleEast = CesiumTools.clampLongitude(
              CesiumTools.avoidExtremeLongitudes(-Math.PI, false)
            )
            westernRectangle = new Cesium.Rectangle(
              westernRectangleWest,
              newSouth,
              westernRectangleEast,
              newNorth
            )
          }

          let conditionLastLongitudeOver180andMoreThan1 =
            lastLon + this.lonStep / 2 >
            CesiumTools.avoidExtremeLatitudes(Math.PI, false)

          let conditionLastLongitudeOver180andOnly1 =
            this.lonStep === 0 &&
            (newValueEastWith1longitude >
              CesiumTools.avoidExtremeLatitudes(Math.PI, false) ||
              Math.abs(
                newValueEastWith1longitude -
                  CesiumTools.avoidExtremeLatitudes(Math.PI, false)
              ) < 1e-3)

          if (
            conditionLastLongitudeOver180andMoreThan1 ||
            conditionLastLongitudeOver180andOnly1
          ) {
            let widthRectangle = conditionLastLongitudeOver180andMoreThan1
              ? lastLon + this.lonStep / 2
              : newValueEastWith1longitude
            needEasternRectangle = true
            easternRectangleWest = CesiumTools.clampLongitude(
              CesiumTools.avoidExtremeLongitudes(Math.PI, false)
            )
            easternRectangleEast = CesiumTools.clampLongitude(widthRectangle)
            easternRectangle = new Cesium.Rectangle(
              easternRectangleWest,
              newSouth,
              easternRectangleEast,
              newNorth
            )
          }

          rectangleContainingMesh = new Cesium.Rectangle(
            newWest,
            newSouth,
            newEast,
            newNorth
          )
        }

        rectangleInstances.push(
          this.createRectangleGeometryInstance(rectangleContainingMesh)
        )
        if (needWesternRectangle) {
          rectangleInstances.push(
            this.createRectangleGeometryInstance(westernRectangle)
          )
        }
        if (needEasternRectangle) {
          rectangleInstances.push(
            this.createRectangleGeometryInstance(easternRectangle)
          )
        }
        this.flyToRectangle(rectangleContainingMesh)
      })

      const rectanglesPrimitive = new Cesium.Primitive({
        geometryInstances: rectangleInstances,
        appearance: new Cesium.PerInstanceColorAppearance({
          flat: true,
          translucent: true
        })
      })

      this.meshesPrimitiveCollection.add(rectanglesPrimitive)
    },

    createRectangleGeometryInstance(rectangle) {
      return new Cesium.GeometryInstance({
        geometry: new Cesium.RectangleGeometry({
          rectangle: rectangle,
          vertexFormat: Cesium.EllipsoidSurfaceAppearance.VERTEX_FORMAT,
          granularity: Cesium.Math.RADIANS_PER_DEGREE * 6,
          extrudedHeight: 0
        }),
        attributes: {
          color: new Cesium.ColorGeometryInstanceAttribute(1, 0, 0, 0.3),
          show: new Cesium.ShowGeometryInstanceAttribute(true)
        }
      })
    },

    addMeshPoints(pointsList) {
      if (!pointsList || !pointsList.length) {
        return
      }
      let pointPosCart = new Cesium.Cartesian3()
      pointsList.forEach((point) => {
        pointPosCart = Cesium.Cartesian3.fromRadians(point.lon, point.lat, 0)
        this.meshPointsPC.add({
          show: true,
          position: pointPosCart,
          pixelSize: 2.0,
          color: Cesium.Color.CYAN.withAlpha(0.6),
          scaleByDistance: new Cesium.NearFarScalar(1e3, 2.5, 1e7, 1)
        })
      })
    },

    drawLinesFromPointsList(pointsList, closeLines) {
      if (!this.pointsList || !this.pointsList.length) {
        return
      }
      let positions = []
      let polylineInstances = []
      let pointPosCart = new Cesium.Cartesian3()
      let point
      let i = 0
      let k = 0
      const [latNumber, lonNumber] = [
        this.numberOfLatitudes,
        this.numberOfLongitudes
      ]
      const lonOffset =
        this.lonStep === 0 ? this.defaultRectangleWidth / 2 : this.lonStep / 2
      let latOffset

      // horizontal lines
      this.drawFirstHorizontalLine(pointsList, polylineInstances, lonNumber)
      for (i = 0; i < latNumber; i++) {
        latOffset = this.latitudeSteps[i] / 2
        point = pointsList[i * lonNumber]
        let shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
        pointPosCart = Cesium.Cartesian3.fromRadians(
          CesiumTools.avoidExtremeLongitudes(point.lon - lonOffset, false),
          CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
          0
        )
        positions.push(pointPosCart)
        for (k = 0; k < lonNumber; k++) {
          shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
          point = pointsList[i * lonNumber + k]
          pointPosCart = Cesium.Cartesian3.fromRadians(
            CesiumTools.avoidExtremeLongitudes(point.lon, false),
            CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
            0
          )
          positions.push(pointPosCart)
        }
        point = pointsList[i * lonNumber + k - 1]
        pointPosCart = Cesium.Cartesian3.fromRadians(
          CesiumTools.avoidExtremeLongitudes(point.lon + lonOffset, false),
          CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
          0
        )
        positions.push(pointPosCart)
        if (closeLines) {
          point = pointsList[i * lonNumber]
          shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
          positions.push(
            Cesium.Cartesian3.fromRadians(
              CesiumTools.avoidExtremeLongitudes(point.lon, false),
              CesiumTools.avoidExtremeLatitudes(shiftedLat, false)
            )
          )
        }
        if (positions.length < 2) {
          break
        }
        polylineInstances.push(
          new Cesium.GeometryInstance({
            geometry: new Cesium.PolylineGeometry({
              positions: positions,
              arcType: Cesium.ArcType.RHUMB
            }),
            attributes: {
              color: Cesium.ColorGeometryInstanceAttribute.fromColor(
                Cesium.Color.WHITE.withAlpha(0.5)
              )
            }
          })
        )
        positions = []
      }

      // vertical lines
      for (i = 0; i < lonNumber; i++) {
        point = pointsList[i]
        latOffset = this.latitudeSteps[0] / 2
        let shiftedLat = Math.max(point.lat - latOffset, -Math.PI / 2)
        pointPosCart = Cesium.Cartesian3.fromRadians(
          CesiumTools.avoidExtremeLongitudes(point.lon - lonOffset),
          shiftedLat
        )
        positions.push(pointPosCart)
        for (k = 0; k < latNumber; k++) {
          latOffset = this.latitudeSteps[k] / 2
          point = pointsList[k * lonNumber + i]
          shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
          pointPosCart = Cesium.Cartesian3.fromRadians(
            CesiumTools.avoidExtremeLongitudes(point.lon - lonOffset),
            point.lat
          )
          positions.push(pointPosCart)
        }
        point = pointsList[(k - 1) * lonNumber + i]
        shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
        pointPosCart = Cesium.Cartesian3.fromRadians(
          CesiumTools.avoidExtremeLongitudes(point.lon - lonOffset),
          shiftedLat
        )
        positions.push(pointPosCart)
        if (positions.length < 2) {
          break
        }
        polylineInstances.push(
          new Cesium.GeometryInstance({
            geometry: new Cesium.PolylineGeometry({
              positions: positions,
              arcType: Cesium.ArcType.GEODESIC
            }),
            attributes: {
              color: Cesium.ColorGeometryInstanceAttribute.fromColor(
                Cesium.Color.WHITE.withAlpha(0.5)
              )
            }
          })
        )
        positions = []
      }
      this.drawLastVerticalLine(
        pointsList,
        polylineInstances,
        lonNumber,
        latNumber
      )
      let polylinePrimitive = new Cesium.Primitive({
        geometryInstances: polylineInstances,
        appearance: new Cesium.PolylineColorAppearance({
          translucent: true
        })
      })
      this.longitudeLinesPC.add(polylinePrimitive)
    },

    drawFirstHorizontalLine(pointsList, polylineInstances, lonNumber) {
      let positions = []
      let k = 0
      let pointPosCart = new Cesium.Cartesian3()
      const lonOffset =
        this.lonStep === 0 ? this.defaultRectangleWidth / 2 : this.lonStep / 2
      const latOffset = this.latitudeSteps[0] / 2

      let point = pointsList[0]
      let shiftedLat = Math.max(point.lat - latOffset, -Math.PI / 2)
      pointPosCart = Cesium.Cartesian3.fromRadians(
        CesiumTools.avoidExtremeLongitudes(point.lon - lonOffset, false),
        CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
        0
      )
      positions.push(pointPosCart)
      for (k = 0; k < lonNumber; k++) {
        point = pointsList[k]
        shiftedLat = Math.max(point.lat - latOffset, -Math.PI / 2)
        pointPosCart = Cesium.Cartesian3.fromRadians(
          CesiumTools.avoidExtremeLongitudes(point.lon, false),
          CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
          0
        )
        positions.push(pointPosCart)
      }
      point = pointsList[k - 1]
      pointPosCart = Cesium.Cartesian3.fromRadians(
        CesiumTools.avoidExtremeLongitudes(point.lon + lonOffset, false),
        CesiumTools.avoidExtremeLatitudes(shiftedLat, false),
        0
      )
      positions.push(pointPosCart)

      polylineInstances.push(
        new Cesium.GeometryInstance({
          geometry: new Cesium.PolylineGeometry({
            positions: positions,
            arcType: Cesium.ArcType.RHUMB
          }),
          attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(
              Cesium.Color.WHITE.withAlpha(0.5)
            )
          }
        })
      )
      positions = []
    },

    drawLastVerticalLine(pointsList, polylineInstances, lonNumber, latNumber) {
      let positions = []
      let k = 0
      let pointPosCart = new Cesium.Cartesian3()
      const lonOffset =
        this.lonStep === 0 ? this.defaultRectangleWidth / 2 : this.lonStep / 2

      let latOffset = this.latitudeSteps[0] / 2
      let point = pointsList[k * lonNumber + lonNumber - 1]
      let shiftedLat = Math.max(point.lat - latOffset, -Math.PI / 2)
      pointPosCart = Cesium.Cartesian3.fromRadians(
        point.lon + lonOffset,
        shiftedLat
      )
      positions.push(pointPosCart)
      for (k = 0; k < latNumber; k++) {
        point = pointsList[k * lonNumber + lonNumber - 1]
        pointPosCart = Cesium.Cartesian3.fromRadians(
          point.lon + lonOffset,
          point.lat
        )
        positions.push(pointPosCart)
      }
      latOffset = this.latitudeSteps[this.numberOfLatitudes - 1] / 2
      point = pointsList[(k - 1) * lonNumber + lonNumber - 1]
      shiftedLat = Math.min(point.lat + latOffset, Math.PI / 2)
      pointPosCart = Cesium.Cartesian3.fromRadians(
        point.lon + lonOffset,
        shiftedLat
      )
      positions.push(pointPosCart)
      polylineInstances.push(
        new Cesium.GeometryInstance({
          geometry: new Cesium.PolylineGeometry({
            positions: positions,
            arcType: Cesium.ArcType.GEODESIC
          }),
          attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(
              Cesium.Color.WHITE.withAlpha(0.5)
            )
          }
        })
      )
      positions = []
    },

    getCoordinatesFromMesh(mesh) {
      const west = parseFloat(mesh.westernmostLongitude)
      const south = parseFloat(mesh.southernmostLatitude)
      const east = parseFloat(mesh.easternmostLongitude)
      const north = parseFloat(mesh.northernmostLatitude)
      if (isNaN(west) || isNaN(south) || isNaN(east) || isNaN(north)) {
        return
      }
      return {
        west: west,
        south: south,
        east: east,
        north: north
      }
    },

    flyToRectangle(rectangle = this.rectangleSelector) {
      if (!this.cameraShouldFly) {
        return
      }
      if (
        this.viewer.scene.mode !== Cesium.SceneMode.MORPHING &&
        !this.mouseDown
      ) {
        let sceneModeDistanceFactor =
          this.viewer.scene.mode === Cesium.SceneMode.SCENE2D ? 2 : 1
        const cartoCenter = Cesium.Rectangle.center(rectangle)
        let maxWidthHeight =
          Math.max(
            Cesium.Rectangle.computeWidth(rectangle),
            Cesium.Rectangle.computeHeight(rectangle)
          ) * 6e6
        cartoCenter.height =
          Math.min(maxWidthHeight, 1e7) * sceneModeDistanceFactor
        this.viewer.camera.flyTo({
          destination: Cesium.Cartographic.toCartesian(cartoCenter),
          easingFunction: Cesium.EasingFunction.QUADRATIC_IN
        })
      }
    },

    onMeshUpdate(mesh, isRadians = false) {
      if (!mesh) {
        return
      }
      let meshCopy = { ...mesh }
      meshCopy.westernmostLongitude = !isRadians
        ? CesiumTools.toRadians(mesh.westernmostLongitude)
        : mesh.westernmostLongitude
      meshCopy.easternmostLongitude = !isRadians
        ? CesiumTools.toRadians(mesh.easternmostLongitude)
        : mesh.easternmostLongitude
      meshCopy.southernmostLatitude = !isRadians
        ? CesiumTools.toRadians(mesh.southernmostLatitude)
        : mesh.southernmostLatitude
      meshCopy.northernmostLatitude = !isRadians
        ? CesiumTools.toRadians(mesh.northernmostLatitude)
        : mesh.northernmostLatitude
      meshCopy.numberOfLatitudes = parseFloat(meshCopy.numberOfLatitudes, 10)
      meshCopy.numberOfLongitudes = parseFloat(meshCopy.numberOfLongitudes, 10)
      meshCopy.rangeStepLatitude = parseFloat(meshCopy.rangeStepLatitude)
      meshCopy.rangeStepLongitude = parseFloat(meshCopy.rangeStepLongitude)
      return meshCopy
    },

    getMinMaxLonLat() {
      if (!this.pointsList || !this.pointsList.length) {
        return [-180, 180, -90, 90]
      }
      let point = this.pointsList.reduce((prev, curr) =>
        prev.lat < curr.lat ? prev : curr
      )
      const minLat = point.lat

      point = this.pointsList.reduce((prev, curr) =>
        prev.lat > curr.lat ? prev : curr
      )
      const maxLat = point.lat

      point = this.pointsList.reduce((prev, curr) =>
        prev.lon < curr.lon ? prev : curr
      )
      const minLon = point.lon

      point = this.pointsList.reduce((prev, curr) =>
        prev.lon > curr.lon ? prev : curr
      )
      const maxLon = point.lon
      return [minLon, maxLon, minLat, maxLat]
    },

    createMesh(closeLines) {
      this.addMeshPoints(this.pointsList)
      this.drawLinesFromPointsList(this.pointsList, closeLines)
    },

    removeAllPrimitives() {
      CesiumTools.removePrimitivesFromPC(this.meshesPrimitiveCollection)
      CesiumTools.removePrimitivesFromPC(this.latitudeLinesPC)
      CesiumTools.removePrimitivesFromPC(this.longitudeLinesPC)
      CesiumTools.removePrimitivesFromPC(this.meshPointsPC)
    }
  }
}
